import type { LiveStatus } from "@/mocks/lives";
import { cn } from "@/utils/format";

const STATUS_STYLE: Record<LiveStatus, string> = {
  live: "bg-accent-500 text-background-50",
  upcoming: "bg-primary-500 text-background-50",
  replay: "bg-background-50/85 text-foreground-950 backdrop-blur",
};

const STATUS_TEXT: Record<LiveStatus, string> = {
  live: "直播中",
  upcoming: "即将开播",
  replay: "回看",
};

interface LiveStatusBadgeProps {
  status: LiveStatus;
  size?: "sm" | "md";
  className?: string;
}

export function LiveStatusBadge({ status, size = "sm", className }: LiveStatusBadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full font-label font-medium tracking-wide whitespace-nowrap",
        size === "sm" ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-xs",
        STATUS_STYLE[status],
        className
      )}
    >
      {status === "live" && (
        <span className="relative flex h-1.5 w-1.5 items-center justify-center">
          <span className="absolute inline-flex h-1.5 w-1.5 animate-ping rounded-full bg-background-50/80" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-background-50" />
        </span>
      )}
      {status === "upcoming" && <i className="ri-time-line" aria-hidden="true" />}
      {status === "replay" && <i className="ri-history-line" aria-hidden="true" />}
      {STATUS_TEXT[status]}
    </span>
  );
}

interface SignalMeterProps {
  value: number;
  sound?: boolean;
  className?: string;
  showLabel?: boolean;
}

export function SignalMeter({ value, sound = true, className, showLabel = false }: SignalMeterProps) {
  const level = value >= 95 ? 4 : value >= 90 ? 3 : value >= 85 ? 2 : 1;
  return (
    <span
      className={cn("inline-flex items-center gap-2", className)}
      title={`实时画面信号 ${value}%`}
    >
      <span className="flex h-3.5 items-end gap-0.5" aria-hidden="true">
        {[1, 2, 3, 4].map((bar) => (
          <span
            key={bar}
            className={cn(
              "w-0.5 rounded-sm",
              bar <= level
                ? level >= 3
                  ? "bg-secondary-500"
                  : "bg-primary-500"
                : "bg-foreground-300/50"
            )}
            style={{ height: `${3 + bar * 2.6}px` }}
          />
        ))}
      </span>
      {sound && (
        <i className="ri-volume-up-line text-secondary-500" aria-hidden="true" />
      )}
      {!sound && <i className="ri-volume-mute-line text-foreground-400" aria-hidden="true" />}
      {showLabel && (
        <span className="font-label text-[11px] text-foreground-500">
          信号 {value}%
        </span>
      )}
    </span>
  );
}

interface CategoryChipProps {
  name: string;
  className?: string;
}

export function CategoryChip({ name, className }: CategoryChipProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-secondary-100/80 px-2 py-0.5 text-[11px] font-medium text-secondary-900",
        className
      )}
    >
      <i className="ri-price-tag-3-line" aria-hidden="true" />
      {name}
    </span>
  );
}